import {
  MutableRefObject, useCallback, useMemo, useRef, useState,
} from 'react';
import { useScreenResize } from '@mgm/common/hooks/useResize';
import { Box, Typography, useTheme } from '@mui/material';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import Card from '@mui/material/Card';
import {
  DataGridPro, GridApiPro, GridColDef, useGridApiRef,
} from '@mui/x-data-grid-pro';
import ExcelJS, { Font } from 'exceljs';
import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';
import { MemoizedBaseAvatar } from '@mgm/common/components/base';
import startCase from 'lodash/startCase';
import moment from 'moment-timezone';
import { CustomNoRowsOverlay } from '@mgm/common/components';
import { PrintWrapper } from '../../../../layout/wrapper/PrintWrapper';
import { useTable } from '../../../../hooks/useTable';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { getCurrencyByCode, getIsPrintMode, getPlatformData } from '../../../../store/common/selectors';
import { getPromotersTotals } from '../../../../store/orders/selectors';
import { MemoizedFilterTabs } from '../../../../components/base';
import { DEFAULT_CURRENCY, INDEX_TO_COLOR } from '../../../../data';
import { getMembers } from '../../../../store/team/selectors';
import { useCommon } from '../../../../hooks/useCommon';
import { getTokenDetails } from '../../../../store/authenticate/selectors';
import { OrdersCountLoader, OrdersTableHeader } from '../../Shared/components';
import { useCommonOrders } from '../../../../hooks/useCommonOrders';

const PromotersSummaryTable = () => {
  const componentRef = useRef<HTMLDivElement | null>(null);
  const { pageHeight, resizableDiv } = useScreenResize(59);
  const isPrintMode = useAppSelector(getIsPrintMode);
  const [filterStatus, setFilterStatus] = useState(0);
  const promotersTotal = useAppSelector(getPromotersTotals);
  const members = useAppSelector(getMembers);
  const { locationNamesById } = useCommon();
  const { settings } = useAppSelector(getTokenDetails);
  const { currencies } = useAppSelector(getPlatformData);

  const {
    setIsFetchByDate,
  } = useCommonOrders({
    prevTab: '3',
  });

  const { t } = useTranslation(['common', 'inputs', 'orders', 'timesheets', 'commissions', 'dashboard']);

  const getCurrency = useAppSelector(getCurrencyByCode);

  const rowsData = useMemo(() => {
    const rows: any[] = [];

    // eslint-disable-next-line no-restricted-syntax
    for (const member of promotersTotal) {
      const email = members.find((m) => m.name === member.name)?.email || '';

      // eslint-disable-next-line no-restricted-syntax
      for (const [date, dayData] of Object.entries(member.days)) {
        const {
          locationName, locationId, totalHours, totalOrders, commissions, start,
        } = dayData;

        rows.push({
          id: `${email}-${locationId}-${date}`,
          name: member.name,
          locationId: `${String(locationId)}-${email}` || null,
          locationName,
          totalHours,
          totalOrders,
          amount: member.amount,
          currency: member.currency,
          dateNetSales: dayData.totalNetSales,
          commissions,
          color: locationNamesById[locationId]?.color,
          dates: moment(start).format(settings?.ui?.dateFormat),
          totalCommissions: member.totalCommissions,
        });
      }
    }

    return rows;
  }, [promotersTotal, locationNamesById]);

  const apiRef = useGridApiRef() as MutableRefObject<GridApiPro>;
  const {
    localText, handleScrollTable, exporting,
  } = useTable(apiRef);

  const [isFullTable, setIsFullTable] = useState(false);

  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);

  const handleFilterStatus = useCallback((newValue: number) => {
    setFilterStatus(newValue);
  }, []);

  const tabs = useMemo(() => {
    const locationMap = new Map<
    string,
    {
      value: number;
      label: string;
      color: string;
      members: Set<string>;
    }
    >();

    let colorIndex = 0;

    promotersTotal.forEach((member) => {
      Object.values(member.days).forEach((day) => {
        const key = `${day.locationId}`;

        if (!locationMap.has(key)) {
          locationMap.set(key, {
            value: day.locationId,
            label: day.locationName,
            color: INDEX_TO_COLOR[colorIndex],
            members: new Set([member.name]),
          });
          colorIndex += 1;
        } else {
          locationMap.get(key)!.members.add(member.name);
        }
      });
    });

    const baseTabs = Array.from(locationMap.values()).map((loc) => ({
      value: loc.value,
      label: loc.label,
      color: loc.color,
      count: loc.members.size, // number of unique members in location
    }));

    baseTabs.unshift({
      value: 0,
      label: t('all', { ns: 'common' }),
      color: 'info',
      count: new Set(promotersTotal.map((m) => m.name)).size,
    });

    return baseTabs;
  }, [promotersTotal]);

  const filteredRows = useMemo(() => {
    if (filterStatus === 0) return rowsData;
    return rowsData.filter((row) => {
      const [locationId] = row.locationId.split('-'); // because your row.locationId is like "81725685983-email"
      return Number(locationId) === filterStatus;
    });
  }, [rowsData, filterStatus]);

  const exportCommissionsData = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Commissions');

    const border = {
      top: { style: 'thin' },
      bottom: { style: 'thin' },
      left: { style: 'thin' },
      right: { style: 'thin' },
    };

    const fontConfig: Partial<Font> = {
      name: 'Public Sans Variable',
      size: 11,
    };

    const headerStyle = {
      font: { ...fontConfig, bold: true },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD3D3D3' } },
      alignment: { vertical: 'middle', horizontal: 'center' },
      border,
    };

    const subHeaderStyle = {
      font: { ...fontConfig, bold: true },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD3D3D3' } },
      alignment: { vertical: 'middle', horizontal: 'center' },
      border,
    };

    const cellStyle = {
      font: fontConfig,
      alignment: { vertical: 'middle', horizontal: 'center' },
      border,
    };

    const totalRowStyle = {
      ...cellStyle,
      fill: {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD3D3D3' },
      },
      font: {
        ...fontConfig,
        bold: true,
      },
    };

    worksheet.addRow([
      t('name', { ns: 'inputs' }),
      t('totalHours', { ns: 'timesheets' }),
      t('totalOrders', { ns: 'timesheets' }),
      t('totalSales', { ns: 'commissions' }),
      t('totalCommissions', { ns: 'commissions' }),
    ]).eachCell((cell: any) => {
      // eslint-disable-next-line no-param-reassign
      cell.style = headerStyle;
    });

    // eslint-disable-next-line no-restricted-syntax
    for (const member of promotersTotal) {
      const totalHours = Object.values(member.days).reduce((sum, d) => sum + (d.totalHours || 0), 0);
      const totalOrders = Object.values(member.days).reduce((sum, d) => sum + (d.totalOrders || 0), 0);
      const totalNetSales = Object.values(member.days).reduce((sum, d) => sum + (d.totalNetSales || 0), 0);

      worksheet.addRow([
        member.name,
        totalHours.toFixed(2),
        totalOrders,
        `${getCurrency().mark}${totalNetSales.toFixed(2)}`,
        `${getCurrency().mark}${member.totalCommissions.toFixed(2)}`,
      ]).eachCell((cell: any) => {
        // eslint-disable-next-line no-param-reassign
        cell.style = totalRowStyle;
      });

      worksheet.addRow([]); // spacer

      worksheet.addRow([
        t('location', { ns: 'inputs' }),
        t('date', { ns: 'timesheets' }),
        t('hoursWorked'),
        t('orders', { ns: 'dashboard' }),
        t('netSales', { ns: 'commissions' }),
        t('commission', { ns: 'commissions' }),
      ]).eachCell((cell: any) => {
        // eslint-disable-next-line no-param-reassign
        cell.style = subHeaderStyle;
      });

      // eslint-disable-next-line no-restricted-syntax,@typescript-eslint/no-unused-vars
      for (const [_, dayData] of Object.entries(member.days)) {
        worksheet.addRow([
          dayData.locationName,
          moment(dayData.start).format(settings?.ui?.dateFormat),
          dayData.totalHours.toFixed(2),
          dayData.totalOrders,
          `${getCurrency().mark}${dayData.totalNetSales.toFixed(2)}`,
          `${getCurrency().mark}${dayData.commissions.toFixed(2)}`,
        ]).eachCell((cell: any) => {
          // eslint-disable-next-line no-param-reassign
          cell.style = cellStyle;
        });
      }

      worksheet.addRow([]);
      worksheet.addRow([]);
    }

    worksheet.columns.forEach((col) => {
      let maxLength = 10;
      col.eachCell?.({ includeEmpty: true }, (cell) => {
        const val = cell.value?.toString() || '';
        maxLength = Math.max(maxLength, val.length);
      });
      // eslint-disable-next-line no-param-reassign
      col.width = maxLength + 2;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/octet-stream' });
    saveAs(blob, `PromoterCommissions_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const renderTableHeader = useCallback(() => (
    <OrdersTableHeader
      printRef={componentRef}
      handleScrollTable={handleScrollTable}
      apiRef={apiRef}
      setIsFetchByDate={setIsFetchByDate}
      isFullTable={isFullTable}
      setIsFullTable={setIsFullTable}
      handleExport={() => exportCommissionsData()}
    />
  ), [apiRef, isFullTable]);

  const renderCustomNoRowsOverlay = () => <CustomNoRowsOverlay text={t('itLooksLikeThereAreNoData', { ns: 'common' })} />;

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('name', { ns: 'inputs' }),
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="baseline" pt={1.5} height="100%">
          {row.id !== 'summary' && (
            <MemoizedBaseAvatar
              statusTooltipText={t('pendingRegistration', { ns: 'team' })}
              status={row.status}
              avatarFile={row.avatarFile}
              avatarName={row.name}
            />
          )}
          <Typography
            {...(row.id === 'summary' && {
              fontWeight: 600,
            })}
            variant="caption"
          >
            {startCase(row.name)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'locationId',
      headerName: t('location', { ns: 'inputs' }),
      minWidth: 300,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="baseline" pt={2} height="100%">
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: row.color,
              marginRight: 1,
            }}
          />
          <Typography variant="caption">
            {row.id === 'summary' ? '' : row.locationName || '/'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'dates',
      headerName: t('workingDates', { ns: 'orders' }),
      rowSpanValueGetter: () => null,
      minWidth: 200,
      renderCell: ({ row }) => (
        <Typography variant="caption">{row.id === 'summary' ? '' : row.dates || '-'}</Typography>
      ),
    },
    {
      field: 'hoursWorked',
      headerName: t('hoursWorked'),
      rowSpanValueGetter: () => null,
      minWidth: 200,
      renderCell: ({ row }) => {
        if (row.id === 'summary') return <Typography variant="caption" />;
        const currency = currencies.find((cr) => cr.code === row.currency) || DEFAULT_CURRENCY;

        const hours = row.totalHours?.toFixed(2);
        const amount = (row.amount * (row?.totalHours || 0))?.toFixed(2);

        return (
          <Typography variant="caption">
            {hours}
            {' '}
            <strong>
              (
              {currency.mark}
              {amount}
              )
            </strong>
          </Typography>
        );
      },
    },
    {
      field: 'totalOrders',
      headerName: t('totalOrders', { ns: 'timesheets' }),
      rowSpanValueGetter: () => null,
      minWidth: 200,
      renderCell: ({ row }) => (
        <Typography variant="caption">{row.id === 'summary' ? '' : row.totalOrders || '-'}</Typography>
      ),
    },
    {
      field: 'dateNetSales',
      headerName: `${t('netSales', { ns: 'commissions' })} (${t('date', { ns: 'orders' })})`,
      width: 200,
      rowSpanValueGetter: () => null,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          {row.id !== 'summary'
            && (
              <Typography
                {...(row.id === 'summary' && {
                  fontWeight: 600,
                })}
                variant="caption"
              >
                {getCurrency().mark}
                {' '}
                {row.dateNetSales?.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
            )}
        </Box>
      ),
    },
    {
      field: 'commissions',
      headerName: `${t('commission', { ns: 'commissions' })} (${t('date', { ns: 'orders' })})`,
      width: 200,
      rowSpanValueGetter: () => null,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          {row.id !== 'summary'
            && (
              <Typography
                {...(row.id === 'summary' && {
                  fontWeight: 600,
                })}
                variant="caption"
              >
                {getCurrency().mark}
                {' '}
                {row.commissions?.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
            )}
        </Box>
      ),
    },
    {
      field: 'totalCommissions',
      headerName: t('totalCommissions', { ns: 'commissions' }),
      flex: 1,
      minWidth: 200,
      type: 'number',
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="baseline" pt={2} height="100%" justifyContent="flex-end">
          <Typography
            {...(row.id === 'summary' && {
              fontWeight: 600,
            })}
            variant="caption"
          >
            {getCurrency().mark}
            {' '}
            {row.totalCommissions?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Typography>
        </Box>
      ),
    },
  ];

  return (
    <PrintWrapper componentRef={componentRef}>
      <div ref={resizableDiv}>
        <Card sx={{
          // eslint-disable-next-line no-nested-ternary
          height: exporting ? 'auto' : (isFullTable && filteredRows?.length > 0 ? 'auto' : pageHeight !== 0 && !isPrintMode ? pageHeight : 'auto'),
          pb: 8,
          ...(isMobile && {
            height: 'auto',
            background: 'unset',
            boxShadow: 'unset',
            px: 0,
            pt: 0,
            pb: '30px',
          }),
        }}
        >
          <MemoizedFilterTabs tabs={tabs} onFilterChange={handleFilterStatus} />
          <DataGridPro
            apiRef={apiRef}
            localeText={localText}
            rows={filteredRows}
            columns={columns}
            rowSpanning
            showCellVerticalBorder
            showColumnVerticalBorder
            getRowId={(row) => row.id}
            slots={{
              toolbar: renderTableHeader,
              noRowsOverlay: renderCustomNoRowsOverlay,
              footer: OrdersCountLoader as any,
            }}
            showToolbar
            hideFooterPagination
            disableRowSelectionOnClick
          />
        </Card>
      </div>
    </PrintWrapper>
  );
};

export { PromotersSummaryTable };
